import { routes } from '@semios/app-platform-banyan-route-definitions'
import type moment from 'moment-timezone'
import { useEffect, useState } from 'react'
import { apiFetch } from 'utils/apiFetch'
import { checkAuthorization } from 'utils/checkAuthorization'

export type TUseSprayRecordsResult = {
  data: routes.InsectSprayRecords.Response | null
  loading: boolean
  error: Error | null
}

export const useSprayRecords = ({
  propertyId,
  insectId,
  dateFrom,
  dateTo,
  enabled = true,
  refreshToken = 0,
}: {
  propertyId: number | null
  insectId: number
  dateFrom: moment.Moment
  dateTo: moment.Moment
  enabled?: boolean
  refreshToken?: number
}): TUseSprayRecordsResult => {
  const [data, setData] = useState<routes.InsectSprayRecords.Response | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  // Using moment unix timestamps in deps so dates with the same value but new Moment
  // instances (from per-render filter recomputes) don't trigger redundant refetches.
  const dateFromMs = +dateFrom
  const dateToMs = +dateTo

  useEffect(() => {
    const canView =
      enabled &&
      propertyId !== null &&
      checkAuthorization({ permission: 'VIEW_GROWER_SPRAYS', entity: propertyId })

    if (!canView) {
      setData(null)

      setLoading(false)

      setError(null)

      return
    }

    let cancelled = false

    setLoading(true)

    setError(null)

    const body: routes.InsectSprayRecords.Request = {
      dateFrom: dateFrom.toISOString(),
      dateTo: dateTo.toISOString(),
      propertyId,
      timezone: dateFrom.tz() ?? 'UTC',
      insectId,
    }

    apiFetch({ url: routes.InsectSprayRecords.path, body })
      .then((res) => {
        if (cancelled) return

        setData(res)
      })
      .catch((err) => {
        if (cancelled) return

        setError(err instanceof Error ? err : new Error(String(err)))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [propertyId, insectId, dateFromMs, dateToMs, enabled, refreshToken])

  return { data, loading, error }
}
